import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { PermissionGate } from "@/components/common/PermissionGate";
import { usePermissions } from "@/hooks/usePermissions";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageHeader } from "@/components/common/page-header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Percent, Calendar } from "lucide-react";
import { toast } from "sonner";
import { adminBillingCycleDiscountService } from "@/services/admin-billing-cycle-discount.service";
import {
  createBillingCycleDiscountSchema,
  updateBillingCycleDiscountSchema,
} from "@/schemas/adminBillingCycleDiscount.schema";
import type { CreateBillingCycleDiscountFormData } from "@/schemas/adminBillingCycleDiscount.schema";
import type {
  BillingCycleDiscountListDto,
  CreateBillingCycleDiscountRequest,
  UpdateBillingCycleDiscountRequest,
} from "@/types/billing-cycle-discount";

function getMonthsLabel(months: number): string {
  if (months === 1) return "1 Month";
  if (months % 12 === 0) {
    const years = months / 12;
    return `${months} Months (${years} Year${years > 1 ? "s" : ""})`;
  }
  return `${months} Months`;
}

export function BillingCycleDiscountListPage() {
  const queryClient = useQueryClient();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const { hasRole } = usePermissions();
  const canEditDiscounts = hasRole(["SuperAdmin", "BillingAdmin"]);
  const [editingItem, setEditingItem] =
    useState<BillingCycleDiscountListDto | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<BillingCycleDiscountListDto | null>(null);

  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin", "billing-cycle-discounts"],
    queryFn: () =>
      adminBillingCycleDiscountService.getDiscounts({ page: 1, limit: 50 }),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateBillingCycleDiscountRequest) =>
      adminBillingCycleDiscountService.createDiscount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "billing-cycle-discounts"],
      });
      toast.success("Discount created");
      setIsFormOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateBillingCycleDiscountRequest;
    }) => adminBillingCycleDiscountService.updateDiscount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "billing-cycle-discounts"],
      });
      toast.success("Discount updated");
      setIsFormOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      adminBillingCycleDiscountService.deleteDiscount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "billing-cycle-discounts"],
      });
      toast.success("Discount deleted");
      setDeleteTarget(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const items = response?.data || [];

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Billing Cycle Discounts" />
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-destructive-ink">Failed to load discounts</p>
            <Button onClick={() => refetch()} className="mt-4">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Billing Cycle Discounts"
        subtitle="Configure discounts for longer billing commitments"
        actions={
          !canEditDiscounts
            ? []
            : [
                {
                  label: "Add Discount",
                  onClick: () => {
                    setEditingItem(null);
                    setIsFormOpen(true);
                  },
                  icon: <Plus className="mr-2 h-4 w-4" />,
                },
              ]
        }
      />

      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground">
          Loading...
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            No billing cycle discounts configured. Create one to offer
            commitment discounts.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => (
            <Card
              key={item.id}
              className={`relative overflow-hidden ${!item.isActive ? "opacity-60" : ""}`}
            >
              <div
                className="absolute top-0 left-0 h-1 w-full bg-primary"
                style={{ width: `${item.discountPercent}%` }}
              />
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Badge variant={item.isActive ? "default" : "outline"}>
                    {item.isActive ? "Active" : "Inactive"}
                  </Badge>
                  <div className="flex gap-1">
                    <PermissionGate roles={["SuperAdmin", "BillingAdmin"]}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          setEditingItem(item);
                          setIsFormOpen(true);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setDeleteTarget(item)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive-ink" />
                      </Button>
                    </PermissionGate>
                  </div>
                </div>
                <CardTitle className="text-base">{item.name}</CardTitle>
                <CardDescription>{getMonthsLabel(item.months)}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Percent className="h-5 w-5 text-primary" />
                  <span className="text-3xl font-bold text-primary">
                    {item.discountPercent}%
                  </span>
                </div>
                <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${item.discountPercent}%` }}
                  />
                </div>
                <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  {item.currency}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <DiscountFormDialog
        key={editingItem?.id ?? "create"}
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        item={editingItem}
        onCreate={(data) => createMutation.mutate(data)}
        onUpdate={(id, data) => updateMutation.mutate({ id, data })}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Discount</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Delete "{deleteTarget?.name}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteTarget && deleteMutation.mutate(deleteTarget.id)
              }
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface DiscountFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: BillingCycleDiscountListDto | null;
  onCreate: (data: CreateBillingCycleDiscountRequest) => void;
  onUpdate: (id: string, data: UpdateBillingCycleDiscountRequest) => void;
  isSubmitting: boolean;
}

function DiscountFormDialog({
  open,
  onOpenChange,
  item,
  onCreate,
  onUpdate,
  isSubmitting,
}: DiscountFormDialogProps) {
  const isEdit = !!item;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreateBillingCycleDiscountFormData>({
    resolver: zodResolver(
      isEdit
        ? updateBillingCycleDiscountSchema
        : createBillingCycleDiscountSchema,
    ),
    defaultValues: {
      name: item?.name || "",
      description: item?.description || "",
      months: item?.months || 12,
      discountPercent: item?.discountPercent ?? 15,
      currency: item?.currency || "VND",
      isActive: item?.isActive ?? true,
    },
  });

  const isActiveValue = watch("isActive") ?? true;

  useEffect(() => {
    if (item) {
      reset({
        name: item.name || "",
        description: item.description || "",
        months: item.months || 12,
        discountPercent: item.discountPercent ?? 15,
        currency: item.currency || "VND",
        isActive: item.isActive ?? true,
      });
    } else {
      reset({
        name: "",
        description: "",
        months: 12,
        discountPercent: 15,
        currency: "VND",
        isActive: true,
      });
    }
  }, [item, reset]);

  const handleFormSubmit = (data: CreateBillingCycleDiscountFormData) => {
    if (isEdit) {
      const updateData: UpdateBillingCycleDiscountRequest = {
        name: data.name || undefined,
        description: data.description || undefined,
        discountPercent: data.discountPercent,
        isActive: data.isActive,
      };
      onUpdate(item!.id, updateData);
    } else {
      onCreate(data as CreateBillingCycleDiscountRequest);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Discount" : "Add Discount"}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <DialogDescription>
            {isEdit
              ? "Update billing cycle discount"
              : "Create a new billing cycle discount tier"}
          </DialogDescription>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                {...register("name")}
                placeholder="e.g., Annual Discount"
              />
              {errors.name && (
                <p className="text-sm text-destructive-ink">
                  {errors.name.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                {...register("description")}
                placeholder="Optional description"
              />
              {errors.description && (
                <p className="text-sm text-destructive-ink">
                  {errors.description.message}
                </p>
              )}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Billing Cycle (Months)</Label>
                <Input
                  type="number"
                  min="1"
                  max="120"
                  placeholder="e.g., 12"
                  {...register("months")}
                  disabled={isEdit}
                />
                {errors.months && (
                  <p className="text-sm text-destructive-ink">
                    {errors.months.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Discount %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  {...register("discountPercent")}
                />
                {errors.discountPercent && (
                  <p className="text-sm text-destructive-ink">
                    {errors.discountPercent.message}
                  </p>
                )}
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Input {...register("currency")} disabled={isEdit} />
                {errors.currency && (
                  <p className="text-sm text-destructive-ink">
                    {errors.currency.message}
                  </p>
                )}
              </div>
              <div className="space-y-2 flex items-center gap-2 pt-6">
                <Switch
                  checked={isActiveValue}
                  onCheckedChange={(v) => setValue("isActive", v)}
                />
                <Label>{isActiveValue ? "Active" : "Inactive"}</Label>
              </div>
            </div>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(handleFormSubmit)}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : isEdit ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
